<script lang="ts" setup>
/**
 * Component that wrapps the Bootstrap Modal, both as a component
 * and exposes methods so parents can control it via code.
 */
import { Modal } from 'bootstrap';
import { ButtonComp, IconComp } from '@/components';
import { computed, nextTick, onDeactivated, onUnmounted, ref, unref, watch } from 'vue';
import { useHelper, useKeyboardListener } from '@/composeables';
import type { ModalProps } from './ModalProps';
import { Router } from '@/objects/Router';

/**
 * Exposed properties
 */
const props = withDefaults(defineProps<ModalProps>(), {
    /**
     * Open the modal centered in the screen
     */
    openCentered: true,

    /**
     * The size of the modal
     * @default 'md'
     */
    size: 'md',

    /**
     * If it should not autofill the screen on small devices
     * @default false
     */
    ignorefullscreen: false,

    /**
     * Should close when clickint ESC
     * @default false
     */
    closeOnEscape: false,

    /**
     * If the modal should have a transition or just apear
     * @default false
     */
    disableAnimation: false,

    /**
     * If the modal should not be destroyed when closed
     * @default false
     */
    keepAlive: false,
});

const emit = defineEmits<{
    (e: 'closed'): void;
    (e: 'hide'): void;
    (e: 'show'): void;

    /**
     * Emitted every time the visibility of the modal changes
     */
    (e: 'visible', value: boolean): void;

    (e: 'escapeKeyDown', event: KeyboardEvent): void;
}>();

if (props.closeOnEscape) {
    // we register the keyboard shortcut if the prop is set
    // Add a shortcut key for close on escape
    useKeyboardListener().addKeyboardShortcut({
        on: 'ESCAPE',
        action: () => {
            hide();
        },
    });
}

/**
 * Ref to the DOM element
 */
const modalHtmlElement = ref<HTMLDivElement | null>(null);

const zIndex = computed(() => {
    return modalHtmlElement.value ? parseInt(getComputedStyle(modalHtmlElement.value).zIndex) : 0;
});

let modalInstance: Modal | null = null;

const visible = ref<boolean>(false);

// Helper property to know what place in the stack the modal is, the higher number the higher zindex
let modalIndex: number = 0;

/**
 * Unique identifier for the modal
 */
const modalSymbol = Symbol();

/**
 * Show the modal
 */
const show = () => {
    visible.value = true; // TODO: This flag is problematic if we want it once, flipping it to false will destroy the modal in dom

    if (!modalInstance) {
        nextTick(() => {
            if (modalHtmlElement.value === null) {
                throw new Error('Modal is not mounted yet. This should never happen!');
            }

            modalInstance = Modal.getOrCreateInstance(modalHtmlElement.value, { keyboard: false });
            modalInstance.show();

            // We need to move the z-index f the backdrop if we have stacked modals. This is a bit of DOM manipulation
            const modals = document.querySelectorAll('.modal-backdrop');
            modalIndex = modals.length - 1; // We need to set the index to the last modal
            if (modals.length > 1) {
                const lastBackdrop = modals[modals.length - 1] as HTMLElement;
                if (lastBackdrop) {
                    lastBackdrop.style.zIndex = 1050 + modals.length * 10 + '';

                    // We need to do the same on the modal with the ref modal
                    const modalElement = modalHtmlElement.value as HTMLElement;
                    modalElement.style.zIndex = 1060 + modals.length * 10 + '';
                }
            }
        });
        return;
    }

    modalInstance.show();
};

// Show the modal if initalVisible is true
if (props.initalVisible) {
    show();
}

const hideAndDestroy = async () => {
    await hide();
    destroy();
};

const hide = async () => {
    if (modalInstance) {
        // Since bootstrap is async, we listen for the modal to close and then resolve the hide promise
        return new Promise<void>((resolve) => {
            if (modalHtmlElement.value === null) {
                // If the htmlElement is gone already we probably unmounted.
                // Try to cleanup the modal instance and resolve immediately
                modalInstance?.dispose();
                resolve();
                return;
                // throw new Error('Modal is not mounted yet. This should never happen!');
            }
            modalHtmlElement.value.addEventListener(
                'hidden.bs.modal',
                () => {
                    // here we should resolve
                    resolve();
                },
                { once: true },
            );

            if (!modalInstance) {
                resolve();
                return; // We cant really do anything here
                // throw new Error('Modal instance is null, this should never happen!');
            }

            // We may very well crash when hiding
            // Like if we are hiding from unmounted, we will probably already have removed the modal from the dom,
            // and then this crashes cause bootstrap is expecting to manage the lifetime of its own elements
            modalInstance.hide();
        });
    } else {
        // TODO: I feel like this else should never happen. We call the hide on a modal before it has had time to initalize??

        // This is inside the else since we will emit onHidden when the modal is hidden via the instance, which will set visible to false
        // We cant do this if we actually have a modal instance since that will remove it from the dom before bootstrap gets the chance to do its cleanup
        visible.value = false;
    }

    return Promise.resolve();
};

const onHidden = () => {
    if (!props.keepAlive) {
        destroy();
    }
    visible.value = false;
    emit('closed');
};

const onShown = () => {
    useHelper().setFocus();
};

/**
 * Destroy the modal
 */
const destroy = () => {
    modalInstance?.dispose();
    visible.value = false;
    modalInstance = null;
};

// if we take the modal out of the dom, we need to hide it so the backdrop is removed
onDeactivated(() => hide());
onUnmounted(() => hide());

/**
 * Whenever the modal is shown
 */
const onShow = () => {
    Router.AddOpenModal(modalSymbol);
    emit('show');
};

/**
 * Whenever the modal is hidden
 */
const onHide = () => {
    // if we close modals in wrong order we will get an index above 0 even if no modals are open
    if (modalIndex > 0 && !Router.HasOpenModal) {
        console.warn('Modal closed in wrong order, Router believes no modal is open but the one we just closed had idx above 0');
    }
    Router.RemoveOpenModal(modalSymbol);
    emit('hide');
};

/**
 * Bootstrap always listens for escape and tries to do its own hide, and since this is async,
 * we have always removed the modal htmlelement by the time bootstraps code triggers (if we have closeOnEscape true).
 * This leads to en error in the console
 *
 * So to avoid that we dont let the modal get the escape key at all and handle it ourself
 */
const modalWrapperKeyDownHandler = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') {
        return;
    }

    // Stop the event from propagating to bootstrap
    useHelper().stopPropagation(event);

    emit('escapeKeyDown', event);
};

// Watch the visible property and emit changes
watch(visible, (nv) => emit('visible', nv));

defineExpose({
    show,
    hide,
    hideAndDestroy,
    destroy,
    zIndex,
});

// We unmount if we have a v-if on a modalComp.
// We might not call hide in that case, so this also needs to do the cleanup.
onUnmounted(() => {
    visible.value = false;
    Router.RemoveOpenModal(modalSymbol);
});
</script>

<template>
    <Teleport
        to="body"
        v-if="visible || keepAlive"
    >
        <div
            ref="modalHtmlElement"
            class="modal"
            :class="{ fade: !disableAnimation }"
            data-bs-backdrop="static"
            tabindex="-1"
            v-on="{ 'hidden.bs.modal': onHidden, 'shown.bs.modal': onShown, 'hide.bs.modal': onHide, 'show.bs.modal': onShow }"
            @keydown="modalWrapperKeyDownHandler"
        >
            <div
                class="modal-dialog"
                :class="{
                    'modal-dialog-centered': openCentered,
                    'modal-fullscreen-md-down': !ignorefullscreen,
                    ['modal-' + size]: true,
                    ['modal-' + type]: type !== undefined,
                }"
            >
                <div
                    class="modal-content"
                    :style="[props.minHeight !== undefined ? `min-height: ${props.minHeight}vh;` : '']"
                >
                    <div class="modal-header">
                        <h1 class="modal-title fs-5">
                            <icon-comp
                                v-if="titleIcon"
                                :icon="titleIcon"
                                :class="titleIconClass + ' me-2'"
                            />
                            {{ $tm(title!) }}
                        </h1>
                        <a
                            v-if="!$slots.exitButton"
                            href="#"
                            @click="hide"
                            aria-label="Close"
                            class="btn btn-secondary ms-auto"
                            type="button"
                        >
                            <span>
                                <icon-comp icon="fa-xmark" />
                            </span>
                        </a>
                        <slot name="exitButton"></slot>
                    </div>

                    <div class="modal-body mb-3"><slot name="content"></slot></div>

                    <div
                        class="modal-footer"
                        v-if="$slots.footer || (footerButtons?.length ?? 0) > 0"
                    >
                        <slot name="footer">
                            <ButtonComp
                                v-for="button in footerButtons"
                                :key="button.loopKey"
                                v-bind="button.props"
                                @click="button.action"
                            >
                                {{ button.text }}
                            </ButtonComp>
                        </slot>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>
